import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { DaySummary } from "@/components/journal/DaySummary";
import { MealSection } from "@/components/journal/MealSection";
import { AddFoodSheet } from "@/components/journal/AddFoodSheet";
import { JournalHistory } from "@/components/journal/JournalHistory";
import { MEALS, isoToday, shiftDate, errorKey, mealForNow } from "@/lib/foodlog";

export default function Journal() {
  const { t, lang } = useLang();
  const qc = useQueryClient();
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const [date, setDate] = useState(isoToday());
  const [memberId, setMemberId] = useState("primary");
  const [sheet, setSheet] = useState(null); // meal ouvert dans la feuille d'ajout

  const { data: planData } = useQuery({ queryKey: ["mealplan"], queryFn: async () => (await api.get("/meal-plan")).data });
  const members = planData?.plan?.members || [];
  const mid = members.some((m) => m.member_id === memberId) ? memberId : "primary";
  const { data: day } = useQuery({ queryKey: ["food-day", date, mid], queryFn: async () => (await api.get("/food/day", { params: { date, member_id: mid } })).data });

  const refresh = () => { qc.invalidateQueries({ queryKey: ["food-day"] }); qc.invalidateQueries({ queryKey: ["food-history"] }); qc.invalidateQueries({ queryKey: ["food-weekly"] }); qc.invalidateQueries({ queryKey: ["food-recent"] }); qc.invalidateQueries({ queryKey: ["tracking"] }); qc.invalidateQueries({ queryKey: ["tracking-history"] }); };
  const run = async (fn, okKey) => { try { await fn(); if (okKey) toast.success(t(okKey)); refresh(); } catch (e) { toast.error(t(errorKey(e))); } };
  const actions = {
    onPatch: (id, patch) => run(() => api.patch(`/food/entries/${id}`, patch), "j_updated"),
    onDuplicate: (id) => run(() => api.post(`/food/entries/${id}/duplicate`, {}), "j_added"),
    onDelete: (id) => run(() => api.delete(`/food/entries/${id}`), "j_deleted"),
  };
  const isToday = date === isoToday();
  const dateLabel = new Date(date + "T12:00:00Z").toLocaleDateString(lang, { weekday: "long", day: "numeric", month: "long" });

  return (
    <AppShell>
      <div className="chenia-fade-up flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-sm uppercase tracking-widest text-muted-foreground font-semibold">{t("j_title")}</p>
          <h1 className="font-serif text-3xl sm:text-4xl font-semibold tracking-tight mt-1 capitalize" data-testid="journal-date">{isToday ? t("j_today") : dateLabel}</h1>
          {isToday && <p className="text-sm text-muted-foreground mt-1">{t("j_sub")}</p>}
        </div>
        <div className="flex items-center gap-1.5" data-testid="journal-date-nav">
          <button onClick={() => setDate(shiftDate(date, -1))} aria-label={t("j_prev_day")} className="grid place-items-center h-9 w-9 rounded-full border border-border hover:bg-secondary" data-testid="journal-prev-day"><ChevronLeft className="h-4 w-4" /></button>
          <input type="date" value={date} max={isoToday()} onChange={(e) => e.target.value && setDate(e.target.value)} className="h-9 rounded-full border border-border bg-card px-3 text-sm" data-testid="journal-date-input" />
          <button onClick={() => setDate(shiftDate(date, 1))} disabled={isToday} aria-label={t("j_next_day")} className="grid place-items-center h-9 w-9 rounded-full border border-border hover:bg-secondary disabled:opacity-40" data-testid="journal-next-day"><ChevronRight className="h-4 w-4" /></button>
        </div>
      </div>

      {members.length > 1 && (
        <div className="mt-4 flex flex-wrap gap-1.5" data-testid="journal-member-tabs">
          {members.map((m) => <button key={m.member_id} onClick={() => setMemberId(m.member_id)} data-testid={`journal-member-${m.member_id}`}
            className={`px-3 py-1 rounded-full text-xs font-medium border ${mid === m.member_id ? "bg-foreground text-background border-foreground" : "bg-card border-border hover:bg-secondary"}`}>{m.name}</button>)}
        </div>
      )}

      {day && (
        <div className="mt-6 grid lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2 space-y-4 min-w-0">
            <DaySummary day={day} />
            {MEALS.map((m) => <MealSection key={m} meal={m} entries={day.meals[m]} subtotal={day.by_meal[m]} onAdd={setSheet} actions={actions} />)}
            <p className="text-xs text-muted-foreground" data-testid="journal-attribution">{t("j_attribution")} <a href="https://world.openfoodfacts.org" target="_blank" rel="noopener noreferrer" className="underline underline-offset-4">openfoodfacts.org</a></p>
          </div>
          <div className="space-y-4 min-w-0"><JournalHistory memberId={mid} /></div>
        </div>
      )}

      <div className="h-16 md:h-0" aria-hidden="true" />
      <Button onClick={() => setSheet(mealForNow())} className="fixed bottom-20 right-4 md:bottom-8 md:right-8 z-30 rounded-full h-12 px-5 bg-primary hover:bg-chenia-terracottadark shadow-lg" data-testid="journal-fab-add"><Plus className="h-5 w-5 mr-1" />{t("j_add_food")}</Button>
      {sheet && <AddFoodSheet open={!!sheet} onClose={() => setSheet(null)} date={date} memberId={mid} meal={sheet} onAdded={refresh} />}
    </AppShell>
  );
}
